// Copyright Prashant Vaghela, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { FC, useState } from 'react';
import React from 'react';

import { BreadcrumbGroup, BreadcrumbGroupProps, Container } from '@cloudscape-design/components';

import { resourceCreateBreadcrumbs } from '../../common/breadcrumbs';
import OperationFlowWithProvider from '../../components/reactFlow/react-flow';
import { HelpPanelProvider } from '../commons';
import { CustomAppLayout } from '../commons/common-components';

// --- Type Definition for API Data ---
export interface LineFlowItem {
  autoId: number;
  id: string;
  description: string;
  sequenceNumber: number;
  targets: string[];
  duration?: number;
}

const Breadcrumbs: FC = () => (
  <BreadcrumbGroup
    items={resourceCreateBreadcrumbs as BreadcrumbGroupProps.Item[]} // Type assertion for external breadcrumbs
    expandAriaLabel="Show path"
    ariaLabel="Breadcrumbs"
  />
);

const LineFlow: FC = () => {
  const apiData: LineFlowItem[] = [
    {
      autoId: 1,
      id: 'LnShapeRim',
      description: 'Line Shape Rim',
      sequenceNumber: 0,
      targets: ['LnWheelAssembly'],
    },
    {
      autoId: 2,
      id: 'LnHubAssembly',
      description: 'Line Hub Assembly',
      sequenceNumber: 0,
      duration: 0,
      targets: ['LnWheelAssembly'],
    },
    {
      autoId: 3,
      id: 'LnWheelAssembly',
      description: 'Line Wheel Assembly',
      sequenceNumber: 1,
      duration: 12,
      targets: ['LnMTBFinAssembly', 'LnRoadFinAssembly', 'LnTourFinAssembly', 'LnCrossFinAssembly'],
    },
    {
      autoId: 4,
      id: 'LnPedAssembly',
      description: 'Line Pedal Assembly',
      sequenceNumber: 1,
      duration: 32,
      targets: ['LnMTBFinAssembly', 'LnRoadFinAssembly', 'LnTourFinAssembly', 'LnCrossFinAssembly'],
    },
    {
      autoId: 5,
      id: 'LnMTBCutTubes',
      description: 'Line MTB Cut Tubes',
      sequenceNumber: 3,
      duration: 0,
      targets: ['LnMTBFinAssembly'],
    },
    {
      autoId: 6,
      id: 'LnMTBFinAssembly',
      description: 'Line MTB Final Assembly',
      sequenceNumber: 2,
      duration: 34,
      targets: [],
    },
    {
      autoId: 7,
      id: 'LnRoadCutTubes',
      description: 'Line Road Cut Tubes',
      sequenceNumber: 3,
      targets: ['LnRoadFinAssembly'],
    },
    {
      autoId: 8,
      id: 'LnRoadFinAssembly',
      description: 'Line Road Final Assembly',
      sequenceNumber: 2,
      duration: 18,
      targets: [],
    },
    {
      autoId: 9,
      id: 'LnTourCutTubes',
      description: 'Line Tour Cut Tubes',
      sequenceNumber: 3,
      duration: 8,
      targets: ['LnTourFinAssembly'],
    },
    {
      autoId: 10,
      id: 'LnTourFinAssembly',
      description: 'Line Tour Final Assembly',
      sequenceNumber: 2,
      duration: 31,
      targets: [],
    },
    {
      autoId: 11,
      id: 'LnCrossCutTubes',
      description: 'Line Cross Cut Tubes',
      sequenceNumber: 3,
      duration: 0,
      targets: ['LnCrossFinAssembly'],
    },
    {
      autoId: 12,
      id: 'LnCrossFinAssembly',
      description: 'Line Cross Final Assembly',
      sequenceNumber: 2,
      duration: 0,
      targets: [],
    },
  ];

  const [toolsOpen, setToolsOpen] = useState(false);

  return (
    <HelpPanelProvider value={null}>
      <CustomAppLayout
        contentType="table"
        content={
          <Container disableContentPaddings>
            <div style={{ width: '100%', height: '85vh' }}>
              {/* Pass the explicitly typed apiData */}
              <OperationFlowWithProvider data={apiData} keys="21" />
            </div>
          </Container>
        }
        breadcrumbs={<Breadcrumbs />}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        navigationHide={true}
      />
    </HelpPanelProvider>
  );
};

export default LineFlow;
